// app/queries/catalogue/useCatalogue.ts

import {useSuspenseQuery} from '@tanstack/react-query';

import apiService from '../../transport/apiService';
import {logger} from '../../utils/logger';

import {UseCatalogueParams} from './types';

/**
 * Query hook for catalogue HTML using Suspense
 *
 * @param storeId - Store Salesforce ID
 * @returns Catalogue HTML string (suspends while loading)
 *
 * @throws Error if storeId is missing or HTML is empty
 *
 * @example
 * // Must be wrapped in Suspense boundary
 * const html = useCatalogue({storeId: 'a0Df4000000XyZ1EAK'});
 * return <WebView source={{html}} />;
 */
export const useCatalogue = ({storeId}: UseCatalogueParams): string => {
  const {data} = useSuspenseQuery({
    queryKey: ['catalogue', storeId],

    queryFn: async () => {
      if (!storeId) {
        logger.warn('useCatalogue', 'Missing storeId parameter');
        throw new Error('Store ID is required to load catalogue');
      }

      logger.info('useCatalogue', 'Fetching catalogue HTML', {storeId});

      const html = await apiService.getCatalogueHtmlV2(storeId);

      // Validate response
      if (!html || html.trim() === '') {
        logger.error('useCatalogue', 'Received empty catalogue HTML', {storeId});
        throw new Error('Catalogue HTML is empty');
      }

      return html;
    },

    // Cache configuration inherited from queryClient defaults:
    // - staleTime: 5 minutes
    // - retry: 2 attempts with exponential backoff
    // - refetchOnMount: true (handles store switching)
  });

  return data;
};
